import {Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FilterDescriptor, GroupDescriptor, SortDescriptor} from '@progress/kendo-data-query';
import {NgClass} from '@angular/common';

import {RechercheService} from '../recherche.service';
import {AttenteOperationComponent, BarreOngletsComponent, DescriptionFiltre, DonneesColonne, DonneesColonneBoutonIconeClick, DonneesGrilleSelection, DonneesOnglet, GrilleComponent, GroupeColonne, TailleColonne, TelechargementFichier, VisibiliteColonne, FonteIconeComponent, SimpleLibelleComponent} from '../../../../val-composants-lib/src/public-api';
import {ASeColonne, ASeOnglet} from '../DonneesRecherche';
import {ValApiResultat} from '../../../../../val-ammon-services/val-services-communs-lib/src/public-api';
import {parametresClient, ValLangueService} from '../../../../../val-definitions-communes-lib/src/public-api';
import {ASeDefinitionFiltre, ASeDefinitionTri, ASeExecuterRechercheParametres} from '../ASeExecuterRecherche';
import {ASeMettreAJourOrdreColonnesUtilisateurParametres} from '../ASeMettreAJourOrdreColonnesUtilisateur';
import {ASeMettreAJourTriColonnesUtilisateurParametres, ASeTriColonne} from '../ASeMettreAJourTriColonnesUtilisateur';
import {ASeMettreAJourVisibiliteColonnesUtilisateurParametres, ASeVisibiliteColonne} from '../ASeMettreAJourVisibiliteColonnesUtilisateur';
import {ASeMettreAJourTailleColonneUtilisateurParametres} from '../ASeMettreAJourTailleColonneUtilisateur';
import {ASeFiltreColonne, ASeMettreAJourFiltreColonnesUtilisateurParametres} from '../ASeMettreAJourFiltreColonnesUtilisateur';
import {ASeGroupeColonne, ASeMettreAJourGroupeColonnesUtilisateurParametres} from '../ASeMettreAJourGroupeColonnesUtilisateur';
import {ASeInformationsRecherche, ASeRetournerInformationsRechercheParametres} from '../ASeRetournerInformationsRecherche';
import {BandeauRechercheComponent} from "../bandeau-recherche/bandeau-recherche.component";
import {ASeExecuterRequeteBandeauParametres} from '../ASeExecuterRequeteBandeau';
import {ASeExporterGrilleVersExcelParametres} from '../ASeExporterGrilleVersExcel';

//Composant grille de recherche
@Component (
{
  selector: 'val-grille-recherche',
  imports: 
  [
    NgClass,
    BarreOngletsComponent,
    GrilleComponent,
    AttenteOperationComponent,
    BandeauRechercheComponent,
    FonteIconeComponent,
    SimpleLibelleComponent
  ],
  templateUrl: './grille-recherche.component.html',
  styleUrl: './grille-recherche.component.scss'
})
export class GrilleRechercheComponent implements OnInit
{
  private valLangueService = inject (ValLangueService);
  private destroyRef = inject (DestroyRef);

  //Libellés de la base, par identifiant d’onglet ou de colonne : la traduction repart d’eux à chaque changement de langue
  private libellesBase = new Map<string, string> ();

  //Création
  //  Paramètres
  //    rechercheService : Service recherche
  constructor (private rechercheService: RechercheService) 
  {
    this.selectionChange = new EventEmitter<void> ();
    this.doubleClick = new EventEmitter<void> ();
    this.colonneBoutonIconeClick = new EventEmitter<DonneesColonneBoutonIconeClick> ();
    this.nom = "";
    this.taillePage = 0;
    this.onglets = [];
    this.colonnes = [];
    this.donneesGrille = [];
    this.numeroPageEnCours = 0;
    this.operationEnCours = false;
    this.ongletEnCours = null;
    this.identifiantFocus = null;
    this.identifiantsSelectionnes = [];
    this.executerRechercheParametres = new ASeExecuterRechercheParametres ();
    this.lignesBandeau = [];
    this.tailleBandeau = 0;
    this.identifiantsOngletsBandeau = [];
    this.bandeauEnCours = "";
    this.panelOuvert = false;
    this.largeurPanel = 100;
    this.identifiantOngletInitial = null;
    this.filtresImposes = [];
  }

  //Barre d’onglets
  @ViewChild ("barreOnglets")
  public barreOnglets!: BarreOngletsComponent;

  //Grille
  @ViewChild ("grille")
  public grille!: GrilleComponent;

  //Identifiant de la recherche
  @Input ()
  public identifiantRecherche!: string;

  //Domaine de la recherche
  @Input ()
  public domaine!: string;

  //Url d’ouverture d’une fiche
  @Input ()
  public urlOuvertureFenetreEdition!: string;

  //Largeur du panel
  @Input ()
  public largeurPanel: number;

  //Identifiant de l’onglet à sélectionner au chargement
  //  Facultatif : sans valeur, ou si l’onglet ne figure pas parmi ceux de la recherche, le
  //  premier onglet est sélectionné comme avant. Lu une seule fois, au chargement des onglets
  //  (SU #118071).
  @Input ()
  public identifiantOngletInitial: string | null;

  //Filtres imposés par l’écran appelant
  //  Ajoutés à ceux de l’utilisateur à chaque exécution et à l’export, sans être affichés ni
  //  enregistrés : l’utilisateur ne peut pas les retirer. Sert une grille de détail restreinte
  //  à la ligne sélectionnée d’une grille mère (SU #119790).
  //  Tableau vide (défaut) : comportement inchangé. null : la recherche n’est pas exécutée et
  //  la grille reste vide, tant que l’écran appelant n’a pas fourni de filtre.
  //  Modifiés après le chargement par AppliquerFiltresImposes.
  @Input ()
  public filtresImposes: ASeDefinitionFiltre[] | null;

  //Evénement de changement de sélection
  @Output ()
  public selectionChange: EventEmitter<void>;

  //Evénement double click
  @Output ()
  public doubleClick: EventEmitter<void>;

  //Evénement de clic sur l’icone d’une colonne BTN_ICON
  //  Simple relais de l’événement de la grille : l’écran appelant y traite l’action de la
  //  ligne, la grille de recherche n’ayant aucun moyen de savoir ce que l’icone déclenche.
  @Output ()
  public colonneBoutonIconeClick: EventEmitter<DonneesColonneBoutonIconeClick>;

  //Nom
  public nom: string;

  //Taille page
  public taillePage: number;

  //Onglets
  public onglets: DonneesOnglet[];

  //Colonnes
  public colonnes: DonneesColonne[];
 
  //Données de la grille
  public donneesGrille: any[];

  //Numéro de page en cours
  public numeroPageEnCours: number;

  //Indique si une opération est en cours
  public operationEnCours: boolean;

  //Onglet en cours
  private ongletEnCours: DonneesOnglet | null;

  //Identifiant de la ligne ayant le focus
  public identifiantFocus: any | null;

  //Identifiants des lignes sélectionnées
  public identifiantsSelectionnes: any[];

  //Paramètres d’exécution de la recherche
  public executerRechercheParametres: ASeExecuterRechercheParametres;

  //Lignes du bandeau
  public lignesBandeau: string[];

  //Taille du bandeau
  public tailleBandeau: number;

  //Identifiants des onglets avec bandeau
  public identifiantsOngletsBandeau: string[];

  //Bandeau en cours (identifiant onglet ou recherche)
  public bandeauEnCours: string;

  //Indique si le panel est ouvert
  public panelOuvert: boolean;

  //Initialise le composant
  public async ngOnInit () : Promise<void>
  {
    this.valLangueService.TraductionsChargees$
      .pipe (takeUntilDestroyed (this.destroyRef))
      .subscribe (() => this.TraduireLibelles ());

    await this.RecupererInformationsRecherche ();
    await this.RecupererOngletsRecherche ();
  }

  //Texte d’interface traduit, ou texte d’origine si l’application ne porte pas la clé grille-recherche.<cle>
  //  Paramètres
  //    cle    : Clé sous grille-recherche.
  //    defaut : Texte d’origine
  public Texte (cle: string, defaut: string) : string
  {
    return this.valLangueService.TraduireOuDefaut ("grille-recherche." + cle, defaut);
  }

  //Libellé d’un onglet ou d’une colonne : la clé recherche.<type>.<GUID> de l’application si elle
  //existe, sinon le libellé de la base. Une application sans clé affiche donc la base, inchangée
  //  Paramètres
  //    type        : onglet ou colonne
  //    identifiant : GUID de l’onglet ou de la colonne
  //    libelle     : Libellé de la base
  private LibelleTraduit (type: "onglet" | "colonne", identifiant: string, libelle: string) : string
  {
    if (!identifiant)
      return libelle;

    return this.valLangueService.TraduireOuDefaut ("recherche." + type + "." + identifiant.toLowerCase (), libelle);
  }

  //Réapplique les traductions aux onglets et colonnes déjà chargés (changement de langue sans rechargement)
  private TraduireLibelles () : void
  {
    for (const onglet of this.onglets)
      onglet.libelle = this.LibelleTraduit ("onglet", onglet.identifiant, this.libellesBase.get (onglet.identifiant) ?? onglet.libelle);

    for (const colonne of this.colonnes)
      colonne.libelle = this.LibelleTraduit ("colonne", colonne.identifiant, this.libellesBase.get (colonne.identifiant) ?? colonne.libelle);
  }

  //Récupère les informations de la recherche
  private async RecupererInformationsRecherche () : Promise<void> 
  {
    let aseRetournerInformationsRechercheParametres: ASeRetournerInformationsRechercheParametres;
    let valApiResultat: ValApiResultat<ASeInformationsRecherche>;

    aseRetournerInformationsRechercheParametres = new ASeRetournerInformationsRechercheParametres ();
    aseRetournerInformationsRechercheParametres.identifiant = this.identifiantRecherche;

    try
    {
      valApiResultat = await this.rechercheService.RetournerInformationsRecherche (aseRetournerInformationsRechercheParametres);

      if (valApiResultat.status === "success")
      {
        this.nom = valApiResultat.resultat!.nom;
        this.taillePage = valApiResultat.resultat!.taillePage;
        this.tailleBandeau = valApiResultat.resultat!.tailleBandeau ?? 0;
      }
      else
        this.tailleBandeau = 0;
    }
    catch (erreur)
    {
    }
  }

  //Récupère les onglets de recherche
  private async RecupererOngletsRecherche () : Promise<void> 
  {
    let valApiResultat: ValApiResultat<ASeOnglet[]>;
    let onglets: DonneesOnglet[];

    try
    {
      valApiResultat = await this.rechercheService.RetournerOngletsRecherche (this.identifiantRecherche);

      if (valApiResultat.status === "success")
      {
        onglets = valApiResultat.resultat!.map (og =>
        {
          let onglet: DonneesOnglet;

          if (og.bandeau)
            this.identifiantsOngletsBandeau.push (og.identifiant);

          onglet = new DonneesOnglet ();
          onglet.identifiant = og.identifiant;
          this.libellesBase.set (og.identifiant, og.libelle);
          onglet.libelle = this.LibelleTraduit ("onglet", og.identifiant, og.libelle);
          onglet.couleur = og.couleur;
          onglet.icone = og.icone;
          onglet.iconeCouleur = og.iconeCouleur;
          onglet.selectionne = false;

          return onglet;
        });

        this.onglets = onglets;

        if (this.onglets.length > 0)
        {
          this.barreOnglets.SelectionnerOnglet (this.RetournerOngletInitial ());
        }
      }
    }
    catch (erreur)
    {
    }
  }

  //Retourne l’onglet à sélectionner au chargement
  //  La comparaison ignore la casse : l’API renvoie les GUID en minuscules, l’écran appelant
  //  les écrit comme en base, en majuscules.
  //  Retourne
  //    Onglet demandé par identifiantOngletInitial s’il existe, sinon le premier onglet
  private RetournerOngletInitial () : DonneesOnglet
  {
    let onglet: DonneesOnglet | undefined;

    if (this.identifiantOngletInitial)
      onglet = this.onglets.find ((og) => og.identifiant?.toLowerCase () === this.identifiantOngletInitial!.toLowerCase ());

    return onglet ?? this.onglets[0];
  }

  //Traite la sélection d’un onglet
  //  Paramètres
  //    onglet : Données de l’onglet
  public async ExecuterActionSurChangementOnglet (onglet: DonneesOnglet) : Promise<void> 
  {
    let bandeauOnglet;

    this.operationEnCours = true;

    this.ReinitaliserRecherche ();
    this.grille.groupes = [];
    this.grille.sort = [];

    this.ongletEnCours = onglet;
    this.numeroPageEnCours = 1;

    if (await this.RecupererColonnes ())
    {
      await this.ExecuterRecherche ();

      if (this.tailleBandeau > 0)
      {
        bandeauOnglet = this.identifiantsOngletsBandeau.findIndex (id => id === onglet.identifiant) !== -1;

        if (this.bandeauEnCours === "" || (this.bandeauEnCours === "recherche" && bandeauOnglet) || (this.bandeauEnCours !== "recherche" && this.bandeauEnCours !== onglet.identifiant))
        {
          this.bandeauEnCours = bandeauOnglet ? onglet.identifiant : "recherche";

          await this.RafraichirBandeau ();
        }
      }
    }

    this.operationEnCours = false;
  }

  //Réinitialise la recherche
  public ReinitaliserRecherche () : void
  {
    this.grille.Reinitialiser ();
    this.donneesGrille = [];
    this.numeroPageEnCours = 1;

    this.ReinitialiserCompteurOnglet ();
  }

  //Réinitialise le compteur de lignes de l’onglet en cours
  //  Appelé par ReinitaliserRecherche : le compteur suit le contenu de la grille, qui repart
  //  de la page 1 sur un changement de tri, de filtre, de visibilité de colonne ou un
  //  rafraichissement. Sur un changement d’onglet, ReinitaliserRecherche est appelée avant que
  //  ongletEnCours ne change : c’est donc l’onglet quitté qui perd son compteur, et seul
  //  l’onglet affiché en porte un.
  private ReinitialiserCompteurOnglet () : void
  {
    if (this.ongletEnCours)
    {
      this.ongletEnCours.nombreLignesChargees = null;
      this.ongletEnCours.chargementComplet = false;
    }
  }

  //Met à jour le compteur de lignes chargées de l’onglet en cours
  //  Aucun appel supplémentaire à l’API : le compteur vaut le nombre de lignes déjà rapatriées.
  //  Le chargement est réputé complet dès que la dernière page reçue est incomplète — c’est la
  //  règle qu’applique GrilleComponent.DemanderDonneesSuivante pour arrêter la pagination.
  private MettreAJourCompteurOnglet () : void
  {
    let nombreLignes: number;

    if (!this.ongletEnCours)
      return;

    nombreLignes = this.donneesGrille.length;

    this.ongletEnCours.nombreLignesChargees = nombreLignes;
    this.ongletEnCours.chargementComplet    = this.taillePage <= 0 || nombreLignes === 0 || nombreLignes % this.taillePage !== 0;
  }

  //Récupère les colonnes
  //  Retourne
  //    Vrai si des colonnes existe
  private async RecupererColonnes () : Promise<boolean> 
  {
    let valApiResultat: ValApiResultat<ASeColonne[]>;
    let colonnes: DonneesColonne[];

    this.colonnes = [];

    try
    {
      valApiResultat = await this.rechercheService.RetournerColonnesOnglet (this.ongletEnCours!.identifiant);

      if (valApiResultat.status === "success")
      {
        colonnes = valApiResultat.resultat!.map (col =>
        {
          let colonne: DonneesColonne;

          colonne = new DonneesColonne ();
          colonne.identifiant = col.identifiant;
          colonne.champ = col.champ;
          this.libellesBase.set (col.identifiant, col.libelle);
          colonne.libelle = this.LibelleTraduit ("colonne", col.identifiant, col.libelle);
          colonne.format = col.format;
          colonne.taille = col.taille;
          colonne.visible = col.visible;

          return colonne;
        });

        this.colonnes = colonnes;
        this.grille.colonnes = colonnes;

        this.MettreAJourTriColonnes (valApiResultat.resultat!);
        this.MettreAJourFiltresColonnes (valApiResultat.resultat!);
        this.MettreAJourGroupeColonnes (valApiResultat.resultat!);
      }
    }
    catch (erreur)
    {
    }

    return this.colonnes.length > 0;
  }

  //Rafraichit le bandeau
  private async RafraichirBandeau () : Promise<void> 
  {
    let aseExecuterRequeteBandeauParametres: ASeExecuterRequeteBandeauParametres;
    let valApiResultat: ValApiResultat<string[]>;

    aseExecuterRequeteBandeauParametres = new ASeExecuterRequeteBandeauParametres ();
    aseExecuterRequeteBandeauParametres.domaine = this.domaine;
    aseExecuterRequeteBandeauParametres.identifiantOnglet = this.ongletEnCours!.identifiant;

    try
    {
      valApiResultat = await this.rechercheService.ExecuterRequeteBandeau (aseExecuterRequeteBandeauParametres);

      if (valApiResultat.status === "success")
        this.lignesBandeau = valApiResultat.resultat!;
    }
    catch (erreur)
    {
    }
  }

  //Evénement de rafraichissement du bandeau
  public async RafraichirBandeauEvenement () : Promise<void> 
  {
    this.operationEnCours = true;

    await this.RafraichirBandeau ();

    this.operationEnCours = false;
  }

  //Met à jour les tris des colonnes
  //  Paramètres
  //    aseColonnes : Données des colonnes
  private MettreAJourTriColonnes (aseColonnes: ASeColonne[]) : void 
  {
    let tris: SortDescriptor[];

    tris = aseColonnes.filter ((col) => col.sensTri)
                      .sort ((colA, colB) => colA.ordreTri! < colB.ordreTri! ? -1 : colA.ordreTri! > colB.ordreTri! ? 1 : 0)
                      .map ((col) =>
    {
      return {field: col.champ, dir: col.sensTri! === "asc" ? "asc" : "desc"};
    });

    this.grille.sort = tris;
  }

  //Met à jour les filtres des colonnes
  //  Paramètres
  //    aseColonnes : Données des colonnes
  private MettreAJourFiltresColonnes (aseColonnes: ASeColonne[]) : void 
  {
    aseColonnes.forEach ((col) =>
    {
      if (col.operateurFiltre)
      {
        this.grille.filtres.filters.push ({field: col.champ, value: this.RetournerValeurFiltre (col), operator: col.operateurFiltre});
      }
    });

    this.grille.MettreAJourFiltresEnCours ();
    this.grille.CreerDescriptionsFiltres ();
  }

  //Met à jour les groupes des colonnes
  //  Paramètres
  //    aseColonnes : Données des colonnes
  private MettreAJourGroupeColonnes (aseColonnes: ASeColonne[]) : void 
  {
    let groupes: GroupDescriptor[];

    groupes = aseColonnes.filter ((col) => col.ordreRegroupement)
                         .sort ((colA, colB) => colA.ordreRegroupement! < colB.ordreRegroupement! ? -1 : colA.ordreRegroupement! > colB.ordreRegroupement! ? 1 : 0)
                         .map ((col) =>
    {
      return {field: col.champ, dir: col.sensTriRegroupement! === "asc" ? "asc" : "desc"};
    });

    this.grille.groupes = groupes;
  }

  //Retourne la valeur du filtre
  //  Paramètres
  //    colonnes : Données de la colonne
  //  Retourne
  //    Valeur du filtre
  private RetournerValeurFiltre (colonne: ASeColonne) : any 
  {
    let valeur: any;

    switch (colonne.format)
    {
      case "TXT" :
      case "TXT_COLOR" : valeur = colonne.valeurFiltreTexte;   break;
      case "INT" : valeur = colonne.valeurFiltreInt;     break;
      //DAT_H porte la même valeur de filtre que DAT : seul le format d’affichage diffère.
      //Sans ce cas, un filtre enregistré sur une colonne date-heure n’est pas restitué au
      //chargement de la grille (SU #116860).
      case "DAT" :
      case "DAT_H" : valeur = this.RetournerDateFiltre (colonne.valeurFiltreDate); break;
      case "CUR" :
      case "CUR_COLOR" : valeur = colonne.valeurFiltreDecimal; break;
      case "DEC" : valeur = colonne.valeurFiltreDecimal; break;
      case "CHK" : valeur = colonne.valeurFiltreBoolean; break;
      
      default: valeur = null; break;
    }

    return valeur;
  }

  //Retourne la date d’un filtre enregistré : le jour, à minuit en heure locale
  //  Sans intercepteur de dates (Ammon Admin Services), la valeur arrive en texte et non en Date.
  //  Paramètres
  //    valeur : Valeur enregistrée
  //  Retourne
  //    Date du filtre
  private RetournerDateFiltre (valeur: Date | string | null) : Date | null 
  {
    let jour: RegExpMatchArray | null;

    if (valeur === null || valeur === undefined)
      return null;

    if (valeur instanceof Date)
      return new Date (valeur.getFullYear (), valeur.getMonth (), valeur.getDate ());

    jour = valeur.match (/^(\d{4})-(\d{2})-(\d{2})/);

    return jour ? new Date (Number (jour[1]), Number (jour[2]) - 1, Number (jour[3])) : null;
  }

  //Exécute la recherche
  public async ExecuterRecherche () : Promise<void> 
  {
    let valApiResultat: ValApiResultat<any>;
    let champsGroupes: string[];

    if (this.filtresImposes === null)
      return;

    this.executerRechercheParametres.domaine = this.domaine;
    this.executerRechercheParametres.identifiantOnglet = this.ongletEnCours!.identifiant;
    this.executerRechercheParametres.numeroPage = this.numeroPageEnCours;

    champsGroupes = [];

    this.executerRechercheParametres.definitionsTris = this.CreerDefinitionsTris ();
    this.executerRechercheParametres.definitionsFiltres = this.CreerDefinitionsFiltres ();

    try
    {
      valApiResultat = await this.rechercheService.ExecuterRecherche (this.executerRechercheParametres);

      
      if (valApiResultat.status === "success")
      {
        this.donneesGrille = [... this.donneesGrille, ... valApiResultat.resultat];

        this.MettreAJourCompteurOnglet ();
      }
    }
    catch (erreur)
    {
    }
  }

  //Charge les donnees de la page suivante
  //  Paramètres
  //    numeroPage : Numéro de page
  public async ChargerDonneesPageSuivante (numeroPage: number) : Promise<void> 
  {
    this.operationEnCours = true;

    this.numeroPageEnCours = numeroPage;

    await this.ExecuterRecherche ();

    this.operationEnCours = false;
  }

  //Applique de nouveaux filtres imposés et relance la recherche depuis la première page
  //  Avant le chargement des colonnes, les filtres sont seulement retenus : la première
  //  exécution, sur la sélection de l’onglet, les lira.
  //  Paramètres
  //    filtres : Filtres imposés, ou null pour vider la grille
  public async AppliquerFiltresImposes (filtres: ASeDefinitionFiltre[] | null) : Promise<void>
  {
    this.filtresImposes = filtres;

    if (!this.ongletEnCours || this.colonnes.length === 0)
      return;

    this.operationEnCours = true;

    this.ReinitaliserRecherche ();

    await this.ExecuterRecherche ();

    this.operationEnCours = false;
  }

  //Exécute les actions sur le changement de sélection
  //  Paramètres
  //    donneesGrilleSelection : Données de sélection de la grille
  public ExecuterActionSurChangementSelection (donneesGrilleSelection: DonneesGrilleSelection) : void 
  {
    this.identifiantsSelectionnes = donneesGrilleSelection.identifiantsSelectionnes;
    this.identifiantFocus = donneesGrilleSelection.identifiantFocus;
    this.selectionChange.emit ();
  }

  //Exécute les actions sur le double click
  public ExecuterActionSurDoubleClick () : void 
  {
    if (this.urlOuvertureFenetreEdition)
      this.OuvrirFiche ();
    else
      this.doubleClick.emit ();
  }

  //Ouvre la fiche
  public OuvrirFiche () : void 
  {
    let identifiant: any | null;
    let url: string;
    
    if (this.urlOuvertureFenetreEdition)
    {
      if (this.identifiantFocus)
        identifiant = this.identifiantFocus;
      else
        if (this.identifiantsSelectionnes.length > 0)
          identifiant = this.identifiantsSelectionnes[0];
        else
          identifiant = null;

      if (identifiant)
      {
        url = `${parametresClient.urlBackOffice}/${this.urlOuvertureFenetreEdition}/${identifiant}`;

        window.open (url, "", "toolbar=no,directories=no,status=no,menubar=no,scrollbars=no,resizable=yes ,modal=yes");    
      }
    }
  }

  //Exécute les actions sur le rafraichissement d’un onglet
  //  Paramètres
  //    onglet : Données de l’onglet
  public async ExecuterActionSurRafraichissementOnglet (onglet: DonneesOnglet) : Promise<void> 
  {
    this.operationEnCours = true;

    this.ReinitaliserRecherche ();

    await this.ExecuterRecherche ();

    this.operationEnCours = false;
  }

  //Exécute les actions sur changement de tris
  //  Paramètres
  //    sort : Descriptions des tris
  public async ExecuterActionSurChangementTris (sort: SortDescriptor[]) : Promise<void> 
  {
    this.operationEnCours = true;

    this.ReinitaliserRecherche ();

    await this.ExecuterRecherche ();

    this.operationEnCours = false;

    await this.MettreAJourTriColonnesUtilisateur ();
  }

  //Met à jour le tri des colonnes de l’utilisateur
  private async MettreAJourTriColonnesUtilisateur () : Promise<void> 
  {
    let aseMettreAJourTriColonnesUtilisateurParametres: ASeMettreAJourTriColonnesUtilisateurParametres;
    let donneesColonne: DonneesColonne | undefined;
    let aseTriColonne: ASeTriColonne;

    aseMettreAJourTriColonnesUtilisateurParametres = new ASeMettreAJourTriColonnesUtilisateurParametres ();
    aseMettreAJourTriColonnesUtilisateurParametres.identifiantOnglet = this.ongletEnCours!.identifiant;

    aseMettreAJourTriColonnesUtilisateurParametres.trisColonnes = this.grille.sort.map ((srt, index) =>
    {
      donneesColonne = this.colonnes.find ((col) =>
                       {
                        return col.champ === srt.field;
                       });

      aseTriColonne = new ASeTriColonne ();
      aseTriColonne.identifiant = donneesColonne!.identifiant;
      aseTriColonne.ordre = index + 1;
      aseTriColonne.sens = srt.dir!;

      return aseTriColonne;
    });

    await this.rechercheService.MettreAJourTriColonnesUtilisateur (aseMettreAJourTriColonnesUtilisateurParametres);
  }

  //Exécute les actions sur changement de filtres
  //  Paramètres
  //    descriptionsFiltres : Descriptions des filtres
  public async ExecuterActionSurChangementFiltres (descriptionsFiltres: DescriptionFiltre[]) : Promise<void> 
  {
    this.operationEnCours = true;

    this.ReinitaliserRecherche ();

    await this.ExecuterRecherche ();

    this.operationEnCours = false;

    await this.MettreAJourFiltreColonnesUtilisateur ();
  }

  //Met à jour le filtre des colonnes de l’utilisateur
  private async MettreAJourFiltreColonnesUtilisateur () : Promise<void> 
  {
    let aseMettreAJourFiltreColonnesUtilisateurParametres: ASeMettreAJourFiltreColonnesUtilisateurParametres;
    let donneesColonne: DonneesColonne | undefined;
    let aseFiltreColonne: ASeFiltreColonne;
    let valApiResultat: ValApiResultat<any>;

    aseMettreAJourFiltreColonnesUtilisateurParametres = new ASeMettreAJourFiltreColonnesUtilisateurParametres ();
    aseMettreAJourFiltreColonnesUtilisateurParametres.identifiantOnglet = this.ongletEnCours!.identifiant;

    aseMettreAJourFiltreColonnesUtilisateurParametres.filtreColonnes = this.grille.descriptionsFiltres.map ((dfl) =>
    {
      donneesColonne = this.colonnes.find ((col) =>
                       {
                        return col.champ === dfl.champ;
                       });

      aseFiltreColonne = new ASeFiltreColonne ();
      aseFiltreColonne.identifiant = donneesColonne!.identifiant;
      aseFiltreColonne.valeurFiltreTexte = dfl.valeurTexte;
      aseFiltreColonne.valeurFiltreInt = dfl.valeurInt;
      aseFiltreColonne.valeurFiltreDate = dfl.valeurDate;
      aseFiltreColonne.valeurFiltreDecimal = dfl.valeurDecimale;
      aseFiltreColonne.valeurFiltreBoolean = dfl.valeurBoolean;
      aseFiltreColonne.operateurFiltre = dfl.operateur;

      return aseFiltreColonne;
    });

    try
    {
      valApiResultat = await this.rechercheService.MettreAJourFiltreColonnesUtilisateur (aseMettreAJourFiltreColonnesUtilisateurParametres);
    }
    catch (erreur)
    {
    }
  }

  //Exécute les actions sur le changement d’ordre
  //  Paramètres
  //    identifiantsColonnes : Identifiants des colonnes
  public async ExecuterActionSurChangementOrdres (identifiantsColonnes: string[]) : Promise<void> 
  {
    let valApiResultat: ValApiResultat<any>;
    let aseMettreAJourOrdreColonnesUtilisateurParametres: ASeMettreAJourOrdreColonnesUtilisateurParametres;

    aseMettreAJourOrdreColonnesUtilisateurParametres = new ASeMettreAJourOrdreColonnesUtilisateurParametres ();
    aseMettreAJourOrdreColonnesUtilisateurParametres.identifiantOnglet = this.ongletEnCours!.identifiant;
    aseMettreAJourOrdreColonnesUtilisateurParametres.identifiantsColonnes = identifiantsColonnes;

    try
    {
      valApiResultat = await this.rechercheService.MettreAJourOrdreColonnesUtilisateur (aseMettreAJourOrdreColonnesUtilisateurParametres);
    }
    catch (erreur)
    {
    }
  }

  //Exécute les actions sur le changement de visibilité
  //  Paramètres
  //    visibiliteColonnes : Données de visibilité des colonnes
  public async ExecuterActionSurChangementVisibilite (visibiliteColonnes: VisibiliteColonne[]) : Promise<void> 
  {
    let valApiResultat: ValApiResultat<any>;
    let aseMettreAJourVisibiliteColonnesUtilisateurParametres: ASeMettreAJourVisibiliteColonnesUtilisateurParametres;
    let aseVisibiliteColonne: VisibiliteColonne;
    let champsColonnesMasquees: string[];
    let donneesColonne: DonneesColonne | undefined;

    aseMettreAJourVisibiliteColonnesUtilisateurParametres = new ASeMettreAJourVisibiliteColonnesUtilisateurParametres ();
    aseMettreAJourVisibiliteColonnesUtilisateurParametres.identifiantOnglet = this.ongletEnCours!.identifiant;

    aseMettreAJourVisibiliteColonnesUtilisateurParametres.visibilitesColonnes = visibiliteColonnes.map ((vcl) =>
    {
      aseVisibiliteColonne = new ASeVisibiliteColonne ();
      aseVisibiliteColonne.identifiant = vcl.identifiant;
      aseVisibiliteColonne.visible = vcl.visible;

      return aseVisibiliteColonne;
    });

    try
    {
      this.operationEnCours = true;

      valApiResultat = await this.rechercheService.MettreAJourVisibiliteColonnesUtilisateur (aseMettreAJourVisibiliteColonnesUtilisateurParametres);

      champsColonnesMasquees = visibiliteColonnes
        .filter ((vcl) => !vcl.visible)
        .map ((vcl) =>
        {
          donneesColonne = this.colonnes.find ((col) => col.identifiant === vcl.identifiant);
          return donneesColonne ? donneesColonne.champ : null;
        })
        .filter ((champ): champ is string => champ !== null);

      if (champsColonnesMasquees.length > 0)
      {
        this.grille.filtres.filters = this.grille.filtres.filters.filter ((fd) =>
          !champsColonnesMasquees.includes ((fd as FilterDescriptor).field as string)
        );

        this.grille.MettreAJourFiltresEnCours ();
        this.grille.CreerDescriptionsFiltres ();
      }

      this.ReinitaliserRecherche ();

      await this.ExecuterRecherche ();

      this.operationEnCours = false;
    }
    catch (erreur)
    {
    }
  }

  //Exécute les actions sur changement de taille
  //  Paramètres
  //    tailleColonne : Données de taille d’une colonne
  public async ExecuterActionSurChangementTaille (tailleColonne: TailleColonne) : Promise<void> 
  {
    let valApiResultat: ValApiResultat<any>;
    let aseMettreAJourTailleColonneUtilisateurParametres: ASeMettreAJourTailleColonneUtilisateurParametres;

    aseMettreAJourTailleColonneUtilisateurParametres = new ASeMettreAJourTailleColonneUtilisateurParametres ();
    aseMettreAJourTailleColonneUtilisateurParametres.identifiantOnglet = this.ongletEnCours!.identifiant;
    aseMettreAJourTailleColonneUtilisateurParametres.identifiantColonne = tailleColonne.identifiant;
    aseMettreAJourTailleColonneUtilisateurParametres.taille = tailleColonne.taille;

    try
    {
      valApiResultat = await this.rechercheService.MettreAJourTailleColonneUtilisateur (aseMettreAJourTailleColonneUtilisateurParametres);
    }
    catch (erreur)
    {
    }
  }

  //Exécute les actions sur changement de groupe
  //  Paramètres
  //    groupesColonnes : Données groupe des colonnes
  public async ExecuterActionSurChangementGroupe (groupesColonnes: GroupeColonne[]) : Promise<void> 
  {
    let valApiResultat: ValApiResultat<any>;
    let aseMettreAJourGroupeColonnesUtilisateurParametres: ASeMettreAJourGroupeColonnesUtilisateurParametres;
    let aseGroupeColonne: ASeGroupeColonne;

    this.operationEnCours = true;

    this.ReinitaliserRecherche ();

    await this.ExecuterRecherche ();

    this.operationEnCours = false;

    aseMettreAJourGroupeColonnesUtilisateurParametres = new ASeMettreAJourGroupeColonnesUtilisateurParametres ();
    aseMettreAJourGroupeColonnesUtilisateurParametres.identifiantOnglet = this.ongletEnCours!.identifiant;

    aseMettreAJourGroupeColonnesUtilisateurParametres.groupesColonnes = groupesColonnes.map ((gcl, index) =>
    {
      aseGroupeColonne = new ASeGroupeColonne ();
      aseGroupeColonne.identifiant = gcl.identifiant;
      aseGroupeColonne.ordre = index + 1;
      aseGroupeColonne.sens = gcl.sens;

      return aseGroupeColonne;
    });

    try
    {
      valApiResultat = await this.rechercheService.MettreAJourGroupeColonnesUtilisateur (aseMettreAJourGroupeColonnesUtilisateurParametres);
    }
    catch (erreur)
    {
    }
  }

  //Exporte vers Excel
  public async ExporterVersExcel () : Promise<void> 
  {
    let aseExporterGrilleVersExcelParametres: ASeExporterGrilleVersExcelParametres;
    let valApiResultat: ValApiResultat<string>;

    if (this.filtresImposes === null)
      return;

    this.operationEnCours = true;

    aseExporterGrilleVersExcelParametres = new ASeExporterGrilleVersExcelParametres ();
    aseExporterGrilleVersExcelParametres.domaine = this.domaine;
    aseExporterGrilleVersExcelParametres.identifiantOnglet = this.ongletEnCours!.identifiant;
    aseExporterGrilleVersExcelParametres.definitionsTris = this.CreerDefinitionsTris ();
    aseExporterGrilleVersExcelParametres.definitionsFiltres = this.CreerDefinitionsFiltres ();

    try
    {
      valApiResultat = await this.rechercheService.ExporterGrilleVersExcel (aseExporterGrilleVersExcelParametres);

      if (valApiResultat.status === "success")
        TelechargementFichier.Telecharger (`${this.nom}.xlsx`, valApiResultat.resultat!, "application/vnd.ms-excel");
    }
    catch (erreur)
    {
    }

    this.operationEnCours = false;
  }

  //Réinitialise les filtres et le tri puis relance la recherche
  public async ReinitialiserFiltresEtTri () : Promise<void>
  {
    this.operationEnCours = true;
    this.grille.ReinitialiserFiltresEtTri ();
    this.ReinitaliserRecherche ();
    await this.ExecuterRecherche ();
    this.operationEnCours = false;

    //La réinitialisation vide les filtres et le tri sans émettre filtresChange ni sortChange :
    //il faut donc persister explicitement, sinon les valeurs précédentes sont relues au rechargement
    await this.MettreAJourFiltreColonnesUtilisateur ();
    await this.MettreAJourTriColonnesUtilisateur ();
  }

  //Crée les définitions des tris
  //  Retourne
  //    Définitions des tris
  private CreerDefinitionsTris () : ASeDefinitionTri[] 
  {
    let definitionsTris: ASeDefinitionTri[];
    let aseDefinitionTri: ASeDefinitionTri;
    let champsGroupes: string[];

    champsGroupes = [];

    definitionsTris = this.grille.groupes.map ((grp) =>
    {
      aseDefinitionTri = new ASeDefinitionTri ();
      aseDefinitionTri.champ = grp.field;
      aseDefinitionTri.direction = grp.dir ? grp.dir : "asc";

      champsGroupes.push (grp.field);

      return aseDefinitionTri;
    }).concat (this.grille.sort.filter ((srt) => !champsGroupes.includes (srt.field) && this.colonnes.find ((col) => col.champ === srt.field && col.visible))
    .map (srt =>
    {
      aseDefinitionTri = new ASeDefinitionTri ();
      aseDefinitionTri.champ = srt.field;
      aseDefinitionTri.direction = srt.dir!;

      return aseDefinitionTri;
    }));

    return definitionsTris.concat (this.CreerDefinitionsTrisFiltresImposes (definitionsTris.map ((dtr) => dtr.champ)));
  }

  //Crée les tris sur les champs des filtres imposés
  //  FctVAL_RetournerColonnes ne retient que les colonnes visibles ou triées : un filtre imposé
  //  sur une colonne que l’utilisateur a masquée serait ignoré sans erreur, et la grille de
  //  détail montrerait les lignes de toutes les lignes mères. Un tri sur le champ retient la
  //  colonne. Placé en dernier, il ne change pas l’ordre voulu ; sans autre tri, il est précédé
  //  de PK, l’ordre par défaut du moteur. Un champ déjà trié n’est pas repris : SQL Server
  //  refuse un ORDER BY qui cite deux fois la même colonne.
  //  Paramètres
  //    champsTries : Champs déjà triés (tris utilisateur et regroupements)
  //  Retourne
  //    Définitions des tris
  private CreerDefinitionsTrisFiltresImposes (champsTries: string[]) : ASeDefinitionTri[]
  {
    let definitionsTris: ASeDefinitionTri[];
    let champs: string[];

    champs = [... new Set ((this.filtresImposes ?? []).map ((dfl) => dfl.nomChamp))]
             .filter ((champ) => !champsTries.includes (champ));

    if (champs.length > 0 && champsTries.length === 0)
      champs.unshift ("PK");

    definitionsTris = champs.map ((champ) =>
    {
      let aseDefinitionTri: ASeDefinitionTri;

      aseDefinitionTri = new ASeDefinitionTri ();
      aseDefinitionTri.champ = champ;
      aseDefinitionTri.direction = "asc";

      return aseDefinitionTri;
    });

    return definitionsTris;
  }

  //Crée les définitions des filtres
  //  Retourne
  //    Définitions des filtres
  private CreerDefinitionsFiltres () : ASeDefinitionFiltre[] 
  {
    let definitionsFiltres: ASeDefinitionFiltre[];
    let aseDefinitionFiltre: ASeDefinitionFiltre;

    definitionsFiltres = this.grille.descriptionsFiltres
    .map ((dfl) =>
    {
      aseDefinitionFiltre = new ASeDefinitionFiltre ();
      aseDefinitionFiltre.nomChamp = dfl.champ;
      aseDefinitionFiltre.operateur = dfl.operateur;
      aseDefinitionFiltre.valeurTexte = dfl.valeurTexte;
      aseDefinitionFiltre.valeurInt = dfl.valeurInt;
      aseDefinitionFiltre.valeurDate = dfl.valeurDate;
      aseDefinitionFiltre.valeurDecimale = dfl.valeurDecimale;
      aseDefinitionFiltre.valeurBoolean = dfl.valeurBoolean;

      return aseDefinitionFiltre;
    });

    return definitionsFiltres.concat (this.filtresImposes ?? []);
  }

  //Ouvre / Ferme le panel
  public OuvrirFermerPanel () : void 
  {
    this.panelOuvert = !this.panelOuvert;
  }
}
